/**
 * BEST MISSIONS — the local high-score table for single missions.
 *
 * `PS.EXE`'s own string table (`docs/game/missions.md`) carries the whole
 * screen: `BEST MISSIONS` as a Fly Single Mission grid button, `ALL TIME HIGH
 * SCORE` and `BEST CAREERS` as its two career-mode siblings, a shared header
 * pool the three list screens draw from (`PILOT NAME`, `KILLS`, `SCORE`,
 * `PLANE`, `SCORE`, `VIEW`, `EXIT`), the empty-table line `"The best mission
 * list is empty. Please play a mission."`, and the placement message
 * `"Congratulations, you ranked number %r in best mission scores recorded."`
 * (`docs/game/campaign.md` notes this same wording is reused for the
 * best-careers context too — one shared template, not a copy-paste error).
 * `PILOT NAME` / `KILLS` / `SCORE` is `topAces`'/`BEST CAREERS`' own row (a
 * career has a pilot and a kill tally); a single mission has neither, so
 * `BEST MISSIONS` draws the other pair the pool offers — `PLANE` / `SCORE` —
 * plus the name typed in on the spot, per the manual's *"If you do well in
 * your mission review, you may have a chance to enter your name in the Best
 * Missions top 10 listing"* and *"In any of the missions available under Fly
 * Single Mission, your score will determine your standing in the Best
 * Missions listing"* (`docs/game/manual/04-gameplay.md`). The manual does not
 * restrict the listing to successful missions, so any completed mission —
 * won or lost — is eligible, exactly like `topAces` takes every career
 * regardless of how it ended.
 *
 * Kept generic on purpose: this module knows nothing about mission types.
 * Anything that produces a `MissionDebrief` — this lane's eight catalog
 * types, or a sibling lane's, present or future — scores here the same way.
 */

/** The manual's own top-10 wording, same size as `topAces`. */
export const BEST_MISSIONS_LIMIT = 10;

export interface BestMissionEntry {
  /** Typed in on the spot — a single mission has no persistent pilot to draw one from. */
  readonly name: string;
  readonly missionTitle: string;
  readonly plane: string;
  readonly score: number;
  /** ISO timestamp: the roster's tie-break, and its own display column if a build ever wants one. */
  readonly recordedAt: string;
}

/** Highest score first; an earlier recording keeps its place over a later, equal one. */
function ranked(entries: readonly BestMissionEntry[]): BestMissionEntry[] {
  return [...entries].sort((left, right) => right.score - left.score || left.recordedAt.localeCompare(right.recordedAt));
}

/** The table as the screen draws it: ranked and capped. */
export function bestMissions(entries: readonly BestMissionEntry[], limit = BEST_MISSIONS_LIMIT): readonly BestMissionEntry[] {
  return ranked(entries).slice(0, limit);
}

/**
 * Whether `score` would land inside the top `limit` right now — the gate on
 * the manual's "you may have a chance to enter your name" prompt. A table with
 * room left always qualifies; a full one needs to beat its own last place.
 */
export function qualifiesForBestMissions(
  entries: readonly BestMissionEntry[],
  score: number,
  limit = BEST_MISSIONS_LIMIT,
): boolean {
  const table = bestMissions(entries, limit);
  const last = table[table.length - 1];
  return table.length < limit || last === undefined || score > last.score;
}

export interface BestMissionPlacement {
  readonly entries: readonly BestMissionEntry[];
  /** This entry's 1-based place on the returned table, or `undefined` if it did not make the cut. */
  readonly rank: number | undefined;
}

/** Inserts `entry`, re-ranks, and re-caps — the write side of `qualifiesForBestMissions`. */
export function insertBestMission(
  entries: readonly BestMissionEntry[],
  entry: BestMissionEntry,
  limit = BEST_MISSIONS_LIMIT,
): BestMissionPlacement {
  const table = bestMissions([...entries, entry], limit);
  const index = table.indexOf(entry);
  return { entries: table, rank: index < 0 ? undefined : index + 1 };
}
